import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MobilePageHeader } from "../components/layout/MobileAppChrome";
import { AxiosError } from "axios";
import { cancelOrder, fetchOrders } from "../services/orderService";
import { fetchReturnRequests, submitReturnRequest } from "../services/returnService";
import { Order, ReturnRequest, ReturnRequestType, ReturnResolution } from "../types/store";
import { formatCurrency } from "../utils/currency";
import { toast } from "../utils/toast";
import "../styles/pages/OrdersPage.css";

const RETURN_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const formatStatus = (value: string) =>
  value
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const formatShortDate = (value: string) =>
  new Date(value).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  });

const getDeliveredTimestamp = (order: Order) => {
  if (!order.deliveredAt) {
    return null;
  }

  const timestamp = new Date(order.deliveredAt).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

const canRequestPostDeliveryAction = (order: Order, returnRequest?: ReturnRequest) => {
  if (returnRequest || order.status !== "DELIVERED") {
    return false;
  }

  const deliveredTimestamp = getDeliveredTimestamp(order);
  if (deliveredTimestamp === null) {
    return false;
  }

  return Date.now() - deliveredTimestamp <= RETURN_WINDOW_MS;
};

const formatAddressSource = (value?: string | null) =>
  value ? value.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (character) => character.toUpperCase()) : "";

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof AxiosError) {
    const message = (error.response?.data as { message?: string } | undefined)?.message;
    if (message && message.trim()) {
      return message;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

const getLatestReturnRequest = (returnRequests: ReturnRequest[], orderId: number) =>
  returnRequests
    .filter((request) => request.orderId === orderId)
    .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())[0];

const getOrderStatusHeadline = (order: Order, returnRequest?: ReturnRequest) => {
  if (returnRequest) {
    const requestLabel = returnRequest.requestType === "REPLACEMENT" ? "Replacement" : "Return";

    if (returnRequest.status === "REFUNDED") {
      return "Refund completed";
    }

    if (returnRequest.requestType === "REPLACEMENT" && returnRequest.status === "DELIVERED") {
      return "Replacement delivered";
    }

    return `${requestLabel} ${formatStatus(returnRequest.status).toLowerCase()}`;
  }

  if (order.status === "DELIVERED") {
    return `Delivered, ${formatShortDate(order.deliveredAt ?? order.createdAt)}`;
  }

  return formatStatus(order.status);
};

const getPromiseCopy = (order: Order, returnRequest?: ReturnRequest) => {
  if (returnRequest) {
    if (returnRequest.status === "REFUNDED") {
      return returnRequest.preferredResolution === "WALLET_CREDIT"
        ? "Refund completed as wallet credit"
        : "Refund completed by support";
    }

    if (returnRequest.requestType === "REPLACEMENT" && returnRequest.status === "DELIVERED") {
      return "Replacement completed by support";
    }

    return `${returnRequest.requestType === "REPLACEMENT" ? "Replacement" : "Return"} request is ${formatStatus(returnRequest.status).toLowerCase()}`;
  }

  if (order.status === "DELIVERED") {
    return "Return or replacement is available for 7 days after delivery";
  }

  if (order.status === "CANCELLED") {
    return "This order was cancelled";
  }

  return "We will keep this order updated as it moves forward";
};

const renderAmountOrFree = (amount: number) =>
  amount > 0 ? (
    <strong>{formatCurrency(amount)}</strong>
  ) : (
    <span className="order-price-summary__free">
      <s>{formatCurrency(amount)}</s>
      <strong>Free</strong>
    </span>
  );

const OrderDetailsPage: React.FC = () => {
  const { orderId } = useParams();
  const [orders, setOrders] = useState<Order[]>([]);
  const [returnRequests, setReturnRequests] = useState<ReturnRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [activeReturnType, setActiveReturnType] = useState<ReturnRequestType | null>(null);
  const [returnReason, setReturnReason] = useState("");
  const [returnDescription, setReturnDescription] = useState("");
  const [returnResolution, setReturnResolution] = useState<ReturnResolution>("WALLET_CREDIT");
  const [submittingReturn, setSubmittingReturn] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [ordersResponse, returnResponse] = await Promise.all([fetchOrders(), fetchReturnRequests()]);
        setOrders(ordersResponse);
        setReturnRequests(returnResponse);
      } finally {
        setLoading(false);
      }
    };

    void loadData();
  }, []);

  const order = useMemo(
    () => orders.find((entry) => entry.id === Number(orderId) || String(entry.id) === orderId) ?? null,
    [orderId, orders],
  );

  if (loading) {
    return (
      <section className="shell section page-section orders-page">
        <MobilePageHeader title="Order Details" />
        <div className="store-card empty-state">
          <h1>Loading order...</h1>
        </div>
      </section>
    );
  }

  if (!order) {
    return (
      <section className="shell section page-section orders-page">
        <MobilePageHeader title="Order Details" />
        <div className="store-card empty-state">
          <h1>Order not found</h1>
          <Link className="button" to="/orders">
            Back to orders
          </Link>
        </div>
      </section>
    );
  }

  const returnRequest = getLatestReturnRequest(returnRequests, order.id);
  const primaryItem = order.items[0];
  const canCancelOrder =
    !returnRequest && (order.status === "CONFIRMED" || order.status === "PROCESSING");
  const canOpenReturnActions = canRequestPostDeliveryAction(order, returnRequest);
  const statusHeadline = getOrderStatusHeadline(order, returnRequest);
  const promiseCopy = getPromiseCopy(order, returnRequest);

  const closeReturnModal = () => {
    setActiveReturnType(null);
    setReturnReason("");
    setReturnDescription("");
    setReturnResolution("WALLET_CREDIT");
  };

  const openReturnForm = (requestType: ReturnRequestType) => {
    setActiveReturnType(requestType);
    setReturnReason("");
    setReturnDescription("");
    setReturnResolution(requestType === "REPLACEMENT" ? "REPLACEMENT" : "WALLET_CREDIT");
  };

  const handleReturnSubmit = async () => {
    if (!activeReturnType) {
      return;
    }

    if (!returnReason.trim() || !returnDescription.trim()) {
      toast.error("Please add a reason and short description.");
      return;
    }

    const preferredResolution =
      activeReturnType === "REPLACEMENT"
        ? "REPLACEMENT"
        : returnResolution === "REPLACEMENT"
          ? "WALLET_CREDIT"
          : returnResolution;

    setSubmittingReturn(true);
    try {
      const createdRequest = await submitReturnRequest({
        orderId: order.id,
        requestType: activeReturnType,
        reason: returnReason,
        description: returnDescription,
        preferredResolution,
      });
      setReturnRequests((current) => [createdRequest, ...current]);
      closeReturnModal();
      toast.success("Request submitted successfully.");
    } catch (submissionError) {
      toast.error(getErrorMessage(submissionError, "Unable to submit the request right now."));
    } finally {
      setSubmittingReturn(false);
    }
  };

  const handleCancelOrder = async () => {
    const confirmed = window.confirm("Cancel this order? This action cannot be undone from your account.");
    if (!confirmed) {
      return;
    }

    setCancelling(true);
    try {
      const cancelledOrder = await cancelOrder(order.id);
      setOrders((current) =>
        current.map((entry) => (entry.id === cancelledOrder.id ? cancelledOrder : entry)),
      );
      toast.success("Order cancelled successfully.");
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to cancel this order right now."));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <section className="shell section page-section orders-page order-details-page">
      <MobilePageHeader title="Order Details" />

      <div className="page-header order-details-page__desktop-header">
        <Link className="orders-page__back-button order-details-page__back-button" to="/orders" aria-label="Back to orders">
          <svg
            className="mobile-app-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M15 18 9 12l6-6" />
          </svg>
          <span>Back</span>
        </Link>
        <h1>Order Details</h1>
        <span aria-hidden="true" />
      </div>

      <div className="order-details-clean">
        <div className="order-details-clean__left">
          <div className="order-details-clean__top">
            {primaryItem ? (
              <img src={primaryItem.image} alt={primaryItem.productName} />
            ) : (
              <div className="order-card__image-fallback" aria-hidden="true" />
            )}
            <div>
              <h2>{primaryItem ? primaryItem.productName : `Order ${order.orderNumber}`}</h2>
              <p>{order.items.length} item{order.items.length > 1 ? "s" : ""}</p>
            </div>
          </div>

          <div className="order-details-clean__number">
            <span>Order #{order.orderNumber}</span>
          </div>

          <div className="order-details-status-card">
            <div className="order-details-status-card__head">
              <div>
                <h3>{statusHeadline}</h3>
                <p>{returnRequest ? `Requested on ${formatShortDate(returnRequest.createdAt)}` : `Placed on ${formatShortDate(order.createdAt)}`}</p>
              </div>
              <span
                className={returnRequest || order.status === "DELIVERED" ? "is-success" : "is-progress"}
                aria-hidden="true"
              />
            </div>

            <div className="order-details-status-card__note">
              <span aria-hidden="true">i</span>
              <p>{promiseCopy}</p>
            </div>

            <Link className="order-details-status-card__updates" to={`/orders/${order.id}/updates`}>
              See all updates
            </Link>
          </div>

          {canOpenReturnActions ? (
            <div className="order-details-clean__action-row">
              <button
                className="button button--light"
                type="button"
                onClick={() => openReturnForm("RETURN")}
              >
                Return
              </button>
              <button
                className="button button--light"
                type="button"
                onClick={() => openReturnForm("REPLACEMENT")}
              >
                Replacement
              </button>
            </div>
          ) : null}

          <section className="order-details-section">
            <h2>Ordered products</h2>
            <div className="order-details-products">
              {order.items.map((item) => (
                <div key={`${order.id}-${item.productSlug}`} className="order-details-product-row">
                  {item.image ? <img src={item.image} alt={item.productName} /> : null}
                  <div>
                    <strong>{item.productName}</strong>
                    <span>{item.quantity} x {formatCurrency(item.unitPrice)}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="order-details-clean__right">
          <section className="order-details-section">
            <h2>Price details</h2>
            <div className="order-price-summary order-price-summary--clean">
              <div>
                <span>Listing price</span>
                <strong>{formatCurrency(order.subtotal + order.shippingCost + order.taxAmount)}</strong>
              </div>
              <div>
                <span>Special price</span>
                <strong>{formatCurrency(order.subtotal)}</strong>
              </div>
              <div>
                <span>Delivery charges</span>
                {renderAmountOrFree(order.shippingCost)}
              </div>
              <div>
                <span>Fees</span>
                {renderAmountOrFree(order.taxAmount)}
              </div>
              <div className="order-price-summary__total">
                <span>Total amount</span>
                <strong>{formatCurrency(order.totalAmount)}</strong>
              </div>
            </div>
          </section>

          <section className="order-details-section">
            <h2>Customer details</h2>
            <div className="order-details-info-card">
              <div>
                <p><strong>{order.shippingName}</strong> {order.phone}</p>
              </div>
              <div>
                <p>{order.email}</p>
              </div>
            </div>
          </section>

          <section className="order-details-section">
            <h2>Delivery address</h2>
            <div className="order-details-info-card">
              <div>
                <p><strong>{order.deliveryMode === "STORE_PICKUP" ? "Store pickup" : "Home"}</strong> {order.shippingAddress}, {order.city} {order.postalCode}</p>
              </div>
              {order.addressSource ? (
                <div>
                  <p><strong>Location source</strong> {formatAddressSource(order.addressSource)}</p>
                </div>
              ) : null}
              {order.addressMapUrl ? (
                <div>
                  <p>
                    <strong>Google Maps</strong>{" "}
                    <a href={order.addressMapUrl} target="_blank" rel="noreferrer">
                      Open current address
                    </a>
                  </p>
                </div>
              ) : null}
              {order.deliverySlot ? (
                <div>
                  <p><strong>Delivery slot</strong> {order.deliverySlot}</p>
                </div>
              ) : null}
              {order.priorityNotes ? (
                <div>
                  <p><strong>Delivery instructions</strong> {order.priorityNotes}</p>
                </div>
              ) : null}
            </div>
          </section>

          {canCancelOrder ? (
            <button
              className="button order-card__cancel-button order-details-clean__cancel"
              type="button"
              disabled={cancelling}
              aria-busy={cancelling}
              onClick={() => void handleCancelOrder()}
            >
              {cancelling ? "Cancelling..." : "Cancel Order"}
            </button>
          ) : null}
        </div>
      </div>

      {activeReturnType && canOpenReturnActions ? (
        <div className="order-card__modal-overlay" role="presentation">
          <div
            className="order-card__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="return-request-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="order-card__modal-header">
              <div>
                <span className="eyebrow">
                  {activeReturnType === "REPLACEMENT" ? "Replacement request" : "Return request"}
                </span>
                <h2 id="return-request-modal-title">
                  {activeReturnType === "REPLACEMENT"
                    ? "Request a replacement"
                    : "Request a return"}
                </h2>
              </div>
              <button className="order-card__modal-close" type="button" onClick={closeReturnModal}>
                ×
              </button>
            </div>

            <p className="order-card__modal-copy">
              Order #{order.orderNumber} from {formatShortDate(order.deliveredAt ?? order.createdAt)}
            </p>

            <div className="order-card__return-form order-card__return-form--modal">
              <div className="order-card__return-type-copy">
                {activeReturnType === "REPLACEMENT"
                  ? "You are requesting a replacement for this delivered order."
                  : "You are requesting a return for this delivered order."}
              </div>
              <label>
                Return reason
                <input
                  value={returnReason}
                  onChange={(event) => setReturnReason(event.target.value)}
                  placeholder="Damaged item, wrong item, quality issue..."
                />
              </label>
              <label>
                Preferred resolution
                <select
                  value={returnResolution}
                  onChange={(event) => setReturnResolution(event.target.value as ReturnResolution)}
                  disabled={activeReturnType === "REPLACEMENT"}
                >
                  {activeReturnType === "REPLACEMENT" ? (
                    <option value="REPLACEMENT">Replacement</option>
                  ) : (
                    <>
                      <option value="WALLET_CREDIT">Wallet credit</option>
                      <option value="MANUAL_REFUND">Manual refund</option>
                    </>
                  )}
                </select>
              </label>
              <label>
                Description
                <textarea
                  rows={4}
                  value={returnDescription}
                  onChange={(event) => setReturnDescription(event.target.value)}
                  placeholder="Share what happened after delivery and any item condition notes."
                />
              </label>
              <div className="order-card__return-actions">
                <button
                  className="button"
                  type="button"
                  disabled={submittingReturn}
                  aria-busy={submittingReturn}
                  onClick={() => void handleReturnSubmit()}
                >
                  {submittingReturn
                    ? "Submitting..."
                    : activeReturnType === "REPLACEMENT"
                      ? "Submit replacement request"
                      : "Submit return request"}
                </button>
                <button className="link-button" type="button" onClick={closeReturnModal}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default OrderDetailsPage;
